import classNames from 'classnames';
import { FormikProps, FormikProvider } from 'formik';
import { ReactNode, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { entityPaths } from '@/modules/entity/routes.tsx';
import {
  hasFormikErrors,
  scrollToFormikError,
} from '@/modules/entity/utils/scrollToFormikError.ts';
import { Button, Tooltip } from 'ui';

export interface OnboardingWrapperProps {
  title?: string;
  titleHint?: string;
  children: ReactNode;
  onClickContinue?: () => void;
  onClickSkip?: () => void;
  buttonSkipName?: string;
  buttonContinueHint?: string | ReactNode;
  formik?: FormikProps<any>;
  isLoading?: boolean;
  disableNext?: boolean;
  className?: string;
}

export const OnboardingWrapper = ({
  title,
  children,
  onClickContinue,
  formik,
  isLoading,
  disableNext,
  onClickSkip,
  className,
  buttonSkipName = 'Skip',
  buttonContinueHint,
  titleHint,
}: OnboardingWrapperProps) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const notifiedSubmitCountRef = useRef(0);
  const formErrors = formik?.errors;
  const isValidating = formik?.isValidating ?? false;
  const submitCount = formik?.submitCount ?? 0;

  useEffect(() => {
    if (submitCount < notifiedSubmitCountRef.current) {
      notifiedSubmitCountRef.current = 0;
    }

    if (isValidating || submitCount === notifiedSubmitCountRef.current) return;

    if (!formErrors || !hasFormikErrors(formErrors)) return;

    notifiedSubmitCountRef.current = submitCount;
    scrollToFormikError(formErrors);
    toast.error('Please correct the highlighted fields before continuing.', {
      id: 'onboarding-validation-error',
    });
  }, [formErrors, isValidating, submitCount]);

  const currentStep = Number(params.get('step') ?? 0);
  const currentSubStep = Number(params.get(`sub_step${currentStep}`) ?? 0);
  const currentSubStepRoad = Number(
    params.get(`sub_step_road${currentStep}_${currentSubStep}`) ?? 0,
  );

  const handleBack = () => {
    if (currentSubStepRoad > 0) {
      params.set(`sub_step_road${currentStep}_${currentSubStep}`, String(currentSubStepRoad - 1));
    } else if (currentSubStep > 0) {
      params.set(`sub_step${currentStep}`, String(currentSubStep - 1));
      params.delete(`sub_step_road${currentStep}_${currentSubStep}`);
    } else if (currentStep > 0) {
      params.set('step', String(currentStep - 1));
      params.delete(`sub_step_road${currentStep}_${currentSubStep}`);
      params.delete(`sub_step${currentStep}`);
    } else {
      params.forEach((_, key) => {
        if (key.toLowerCase().includes('step')) {
          params.delete(key);
        }
      });
      navigate(
        { pathname: entityPaths.static.entityList, search: params.toString() },
        { replace: false },
      );
      return;
    }

    setParams(params, { replace: false });
  };

  const Body = (
    <div
      className={classNames(
        'flex flex-col space-y-10',
        {
          'w-full': !className?.includes('w-'),
        },
        className,
      )}
    >
      {title && (
        <Tooltip
          position="up"
          positionHorizontal="center"
          variant="black"
          disabled={!titleHint}
          button={
            <span
              style={{ wordBreak: 'break-word' }}
              className={classNames(
                'sm:text-[48px] sm:leading-[56px] text-[38px] leading-[46px] font-semibold font-[RecifeDisplay] break-words',
                {
                  'cursor-help': !!titleHint,
                },
              )}
            >
              {title}
            </span>
          }
        >
          {titleHint}
        </Tooltip>
      )}
      {children}
      <div className="w-full flex flex-col items-center justify-between gap-4">
        {onClickContinue && (
          <Tooltip
            position="up"
            positionHorizontal="center"
            variant="gray"
            classNameTooltip="border"
            className="w-full"
            button={
              <Button
                className="w-full relative"
                type="submit"
                onClick={onClickContinue}
                disabled={isLoading || disableNext}
              >
                Continue
              </Button>
            }
            disabled={!buttonContinueHint}
          >
            <span>{buttonContinueHint}</span>
          </Tooltip>
        )}
        {onClickSkip && (
          <Button className="w-full" variant="secondary" type="submit" onClick={onClickSkip}>
            {buttonSkipName}
          </Button>
        )}
        <Button className="w-full" variant="secondary" type="button" onClick={handleBack}>
          Back
        </Button>
      </div>
    </div>
  );

  if (formik) {
    return <FormikProvider value={formik}>{Body}</FormikProvider>;
  }

  return <>{Body}</>;
};
